import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { IconType } from "react-icons";
import {
  FiBriefcase,
  FiGrid,
  FiHeart,
  FiHelpCircle,
  FiHome,
  FiInfo,
  FiLogOut,
  FiMessageSquare,
  FiSearch,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { useAuth } from "@/shared/hooks/useAuth";
import { getHomeRouteForRole, getLoginRouteForRole, ROUTES } from "@/constants/routes";
import { getPortalHost, getPortalLoginHref } from "@/utils/portalHost";
import { getProviderPortalScheduleSettings } from "@/modules/portal/provider/api/providerPortal.api";
import { getProviderUnreadMessageCount } from "@/modules/portal/provider/api/providerMessages.api";
import { getMyMessageUnreadCount } from "@/modules/portal/user/providerdetail/api/messages.api";
import { useCookieConsent } from "@/shared/cookies/CookieConsentProvider";
import { getPortalNavigationGroupsForRole, type PortalRole } from "@/shared/navigation/portalNavigation";
import styles from "@/shared/components/layout/Navbar.module.css";

type NavItem = {
  label: string;
  to: string;
};

type UserMenuBadge =
  | { type: "count"; value: number | string }
  | { type: "label"; value: string; variant?: "beta" | "muted" | "primary" };

type UserMenuItem = {
  label: string;
  path?: string;
  onClick?: () => void;
  icon: IconType;
  badge?: UserMenuBadge;
};

type UserMenuGroup = {
  label: string;
  items: UserMenuItem[];
};

const PUBLIC_NAV_ITEMS: NavItem[] = [
  { label: "For Providers", to: ROUTES.providersGrow },
  { label: "Features", to: ROUTES.features },
  { label: "Our Mission", to: "/about" },
  { label: "FAQs", to: ROUTES.faq },
];

const PROVIDER_TOP_NAV_ITEMS: NavItem[] = [
  { label: "Home", to: ROUTES.providersHome },
  { label: "Profile", to: ROUTES.providersProfile },
  { label: "Support", to: ROUTES.providersSupport },
  { label: "Messages", to: ROUTES.providersMessages },
];


function getAccessNavItems(portal: PortalRole): NavItem[] {
  if (portal === "provider" || portal === "staff") {
    return [{ label: "Practice Hub Login", to: getPortalLoginHref("provider") }];
  }

  if (portal === "admin") {
    return [{ label: "Command Center Login", to: getPortalLoginHref("admin") }];
  }

  return [
    { label: "Join as Provider", to: ROUTES.providersGrow },
    { label: "My Care Sign In", to: getPortalLoginHref("patient") },
    { label: "Practice Hub Login", to: getPortalLoginHref("provider") },
  ];
}

function getRoleNavItems(role?: string | null, canManageBookingSchedule = true): NavItem[] {
  const normalizedRole: PortalRole =
    role === "admin"
      ? "admin"
      : role === "provider"
        ? "provider"
        : role === "staff"
          ? "staff"
          : "patient";
  const groups = getPortalNavigationGroupsForRole(normalizedRole, {
    includeDashboardItems: true,
    includeSidebarItems: true,
    canManageBookingSchedule,
  });
  return groups
    .flatMap((group) => group.items)
    .filter((item) => item.showInSidebar)
    .map((item) => ({ label: item.label, to: item.path }));
}

function renderNavLinks(
  items: NavItem[],
  className: string,
  onClick: () => void,
) {
  return items.map((item) => (
    <Link key={item.to} to={item.to} className={className} onClick={onClick}>
      {item.label}
    </Link>
  ));
}

function getTopNavIcon(label: string): IconType {
  switch (label) {
    case "Home":
      return FiHome;
    case "Profile":
      return FiUser;
    case "Support":
      return FiHelpCircle;
    case "Messages":
      return FiMessageSquare;
    case "For Providers":
      return FiBriefcase;
    case "Features":
      return FiGrid;
    case "Our Mission":
      return FiInfo;
    case "FAQs":
      return FiHelpCircle;
    default:
      return FiSearch;
  }
}

function getInitialsFromName(name?: string | null): string {
  const source = (name ?? "").trim();
  if (!source) return "U";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function getRoleLabel(role?: string | null): string {
  if (role === "admin") return "Administrator";
  if (role === "provider" || role === "staff") return "Provider";
  return "Member";
}

function mapNavItemToUserMenuItem(item: NavItem): UserMenuItem {
  const icon =
    item.label === "My Care Sign In"
      ? FiHeart
      : item.label === "Practice Hub Login"
        ? FiBriefcase
        : item.label === "Command Center Login"
          ? FiShield
          : FiGrid;
  return {
    label: item.label,
    path: item.to,
    icon,
  };
}

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { openPreferenceCenter } = useCookieConsent();
  const navigate = useNavigate();
  const location = useLocation();
  const portalHost = getPortalHost();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [accessMenuOpen, setAccessMenuOpen] = useState(false);

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    setAccessMenuOpen(false);
  };

  const handleLogout = () => {
    const roleAtLogout = user?.role;
    logout();
    const destination =
      roleAtLogout === "provider" || roleAtLogout === "staff" || roleAtLogout === "admin"
        ? getLoginRouteForRole(roleAtLogout)
        : ROUTES.home;
    navigate(destination, { replace: true });
    closeAllMenus();
  };

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const role = user?.role;
  const scheduleSettingsQuery = useQuery({
    queryKey: ["provider-portal-schedule-settings"],
    queryFn: getProviderPortalScheduleSettings,
    enabled: role === "provider" || role === "staff",
    staleTime: 30_000,
  });
  const canManageBookingSchedule =
    role === "provider" || role === "staff"
      ? (scheduleSettingsQuery.data?.allowBooking ?? true)
      : true;
  const unreadMessagesQuery = useQuery({
    queryKey: ["navbar-unread-messages", role],
    queryFn: () =>
      role === "provider" || role === "staff"
        ? getProviderUnreadMessageCount()
        : getMyMessageUnreadCount(),
    enabled: isAuthenticated && role !== "admin",
    staleTime: 20_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const unreadMessages = unreadMessagesQuery.data ?? 0;
  const rootRoute = getHomeRouteForRole(role);
  const roleNavItems = useMemo(() => {
    const items = getRoleNavItems(role, canManageBookingSchedule);
    if (unreadMessages <= 0) return items;
    return items.map((item) => {
      const isMessageItem =
        item.to === ROUTES.messages || item.to === ROUTES.providersMessages;
      if (!isMessageItem) return item;
      return { ...item, label: `Messages (${unreadMessages})` };
    });
  }, [canManageBookingSchedule, role, unreadMessages]);
  const primaryNavItems = useMemo<NavItem[]>(() => {
    if (isAuthenticated && role === "provider") {
      return PROVIDER_TOP_NAV_ITEMS;
    }
    return [{ label: "Home", to: isAuthenticated ? rootRoute : ROUTES.home }, ...PUBLIC_NAV_ITEMS];
  }, [isAuthenticated, role, rootRoute]);

  const isActive = (path: string) => location.pathname === path;

  const fallbackDisplayName =
    `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() ||
    user?.email ||
    "User";
  const providerDisplayName = scheduleSettingsQuery.data?.providerName?.trim() || "";
  const profileDisplayName =
    role === "provider" || role === "staff"
      ? providerDisplayName || fallbackDisplayName
      : fallbackDisplayName;
  const initials = useMemo(
    () =>
      getInitialsFromName(profileDisplayName) ||
      getInitialsFromName(fallbackDisplayName) ||
      "U",
    [fallbackDisplayName, profileDisplayName],
  );
  const roleLabel = getRoleLabel(role);
  const anonymousPortalRole: PortalRole =
    portalHost === "admin"
      ? "admin"
      : portalHost === "provider"
        ? "provider"
        : "patient";

  const groupedUserMenu = useMemo<UserMenuGroup[]>(() => {
    const normalizedRole: PortalRole =
      role === "admin"
        ? "admin"
        : role === "provider"
          ? "provider"
          : role === "staff"
            ? "staff"
            : "patient";
    const groups = getPortalNavigationGroupsForRole(normalizedRole, {
      includeDashboardItems: true,
      includeSidebarItems: true,
      canManageBookingSchedule,
    });
    return groups.map((group) => ({
      label: group.label,
      items: group.items
        .filter((item) => item.showInSidebar)
        .map((item) => {
          const withUnread =
            unreadMessages > 0 &&
            (item.path === ROUTES.messages || item.path === ROUTES.providersMessages);
          return {
            label: item.label,
            path: item.path,
            icon: item.icon,
            badge: withUnread ? ({ type: "count", value: unreadMessages } as UserMenuBadge) : undefined,
          };
        }),
    }));
  }, [canManageBookingSchedule, role, unreadMessages]);

  const accessGroups = useMemo<UserMenuGroup[]>(() => [
    {
      label: "Sign In",
      items: getAccessNavItems(anonymousPortalRole).map(mapNavItemToUserMenuItem),
    },
  ], [anonymousPortalRole]);
  const directAccessItem = accessGroups[0]?.items[0];
  const showAccessMenu = anonymousPortalRole === "patient";

  const signOutItem: UserMenuItem = { label: "Sign Out", icon: FiLogOut, onClick: handleLogout };

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <Link to={ROUTES.home} className={styles.logo} onClick={closeAllMenus} aria-label="Go to homepage">
          <img src="/images/confida-care-logo.png" alt="Confida Care" className={styles.logoImage} />
        </Link>

        <div className={styles.links}>
          {primaryNavItems.map((item) => {
            const Icon = getTopNavIcon(item.label);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`${styles.link} ${isActive(item.to) ? styles.active : ""}`}
                onClick={closeAllMenus}
              >
                <span className={styles.linkContent}>
                  <Icon className={styles.linkIcon} />
                  <span>{item.label}</span>
                </span>
              </Link>
            );
          })}
        </div>

        <div className={styles.auth}>
          <button
            type="button"
            className={styles.utilityLink}
            onClick={openPreferenceCenter}
          >
            Cookie Preferences
          </button>
          {isAuthenticated ? (
            <div className={styles.userMenuWrap}>
              <button
                type="button"
                className={styles.userChip}
                onClick={() => setUserMenuOpen((value) => !value)}
              >
                {unreadMessages > 0 ? (
                  <span className={styles.unreadDot} aria-label={`${unreadMessages} unread messages`} />
                ) : null}
                <div className={styles.avatar}>
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt={profileDisplayName}
                      className={styles.avatarImage}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <div className={styles.userMeta}>
                  <span className={styles.userLabel}>Welcome back</span>
                  <span className={styles.userName}>{profileDisplayName}</span>
                </div>
              </button>

              {userMenuOpen && (
                <div className={styles.userMenu}>
                  {groupedUserMenu ? (
                    <>
                      <div className={styles.menuProfileHeader}>
                        <div className={styles.menuAvatar}>
                          {user?.profile_picture_url ? (
                            <img
                              src={user.profile_picture_url}
                              alt={profileDisplayName}
                              className={styles.avatarImage}
                            />
                          ) : (
                            initials
                          )}
                        </div>
                        <div className={styles.menuProfileText}>
                          <span className={styles.menuProfileName}>{profileDisplayName}</span>
                          <span className={styles.menuProfileRole}>{roleLabel}</span>
                        </div>
                      </div>
                      <div className={styles.menuDivider} />
                      {groupedUserMenu.map((group, groupIndex) => (
                        <React.Fragment key={group.label}>
                          <div className={styles.menuSectionLabel}>{group.label}</div>
                          <div className={styles.menuSectionList}>
                            {group.items.map((item) => {
                              const Icon = item.icon;
                              const active = item.path ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) : false;
                              return (
                                <Link
                                  key={item.path ?? item.label}
                                  to={item.path ?? "#"}
                                  className={`${styles.userMenuItem} ${active ? styles.userMenuItemActive : ""}`}
                                  onClick={() => setUserMenuOpen(false)}
                                >
                                  <span className={styles.userMenuIconWrap}><Icon className={styles.userMenuIcon} /></span>
                                  <span className={styles.userMenuItemLabel}>{item.label}</span>
                                  {item.badge ? (
                                    <span
                                      className={`${styles.userMenuBadge} ${
                                        item.badge.type === "count"
                                          ? styles.userMenuBadgeCount
                                          : item.badge.variant === "beta"
                                            ? styles.userMenuBadgeBeta
                                            : styles.userMenuBadgeMuted
                                      }`}
                                    >
                                      {item.badge.value}
                                    </span>
                                  ) : null}
                                </Link>
                              );
                            })}
                          </div>
                          {groupIndex < groupedUserMenu.length - 1 ? <div className={styles.menuDivider} /> : null}
                        </React.Fragment>
                      ))}
                      <div className={styles.menuDivider} />
                      <button
                        type="button"
                        className={styles.userMenuSignOut}
                        onClick={signOutItem.onClick}
                      >
                        <FiLogOut className={styles.userMenuIcon} />
                        <span>{signOutItem.label}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      {renderNavLinks(roleNavItems, styles.userMenuLink, () => setUserMenuOpen(false))}
                      <button
                        type="button"
                        className={styles.userMenuButton}
                        onClick={handleLogout}
                      >
                        Sign Out
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              {showAccessMenu ? (
                <div className={styles.userMenuWrap}>
                  <button
                    type="button"
                    className={styles.accessChip}
                    onClick={() => setAccessMenuOpen((value) => !value)}
                  >
                    Sign In Options
                  </button>

                  {accessMenuOpen && (
                    <div className={styles.userMenu}>
                      {accessGroups.map((group, groupIndex) => (
                        <React.Fragment key={group.label}>
                          <div className={styles.menuSectionLabel}>{group.label}</div>
                          <div className={styles.menuSectionList}>
                            {group.items.map((item) => {
                              const Icon = item.icon;
                              const active = item.path ? location.pathname === item.path : false;
                              return (
                                <Link
                                  key={item.path ?? item.label}
                                  to={item.path ?? "#"}
                                  className={`${styles.userMenuItem} ${active ? styles.userMenuItemActive : ""}`}
                                  onClick={() => setAccessMenuOpen(false)}
                                >
                                  <span className={styles.userMenuIconWrap}><Icon className={styles.userMenuIcon} /></span>
                                  <span className={styles.userMenuItemLabel}>{item.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                          {groupIndex < accessGroups.length - 1 ? <div className={styles.menuDivider} /> : null}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              ) : directAccessItem?.path ? (
                <Link to={directAccessItem.path} className={styles.accessChip} onClick={closeAllMenus}>
                  Sign In
                </Link>
              ) : null}
              {portalHost === "patient" ? (
                <Link to={ROUTES.register} className="btn btn-primary btn-sm">
                  Support My Family&apos;s Health
                </Link>
              ) : null}
            </>
          )}
        </div>

        <button
          className={styles.hamburger}
          onClick={() => setMobileMenuOpen((value) => !value)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className={`${styles.bar} ${mobileMenuOpen ? styles.barOpen1 : ""}`} />
          <span className={`${styles.bar} ${mobileMenuOpen ? styles.barOpen2 : ""}`} />
          <span className={`${styles.bar} ${mobileMenuOpen ? styles.barOpen3 : ""}`} />
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuSection}>
            <div className={styles.menuSectionLabel}>Navigation</div>
            <div className={styles.menuSectionList}>
              {primaryNavItems.map((item) => {
                const Icon = getTopNavIcon(item.label);
                const active = isActive(item.to);
                return (
                  <Link
                    key={`mobile-primary-${item.to}`}
                    to={item.to}
                    className={`${styles.userMenuItem} ${active ? styles.userMenuItemActive : ""}`}
                    onClick={handleMobileLinkClick}
                  >
                    <span className={styles.userMenuIconWrap}><Icon className={styles.userMenuIcon} /></span>
                    <span className={styles.userMenuItemLabel}>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className={styles.mobileDivider} />

          {isAuthenticated ? (
            <>
              <div className={styles.mobileProfileHeader}>
                <div className={styles.menuAvatar}>
                  {user?.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt={profileDisplayName}
                      className={styles.avatarImage}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <div className={styles.menuProfileText}>
                  <span className={styles.menuProfileName}>{profileDisplayName}</span>
                  <span className={styles.menuProfileRole}>{roleLabel}</span>
                </div>
              </div>
              <div className={styles.mobileDivider} />
              {groupedUserMenu.map((group, groupIndex) => (
                <React.Fragment key={`mobile-auth-group-${group.label}`}>
                  <div className={styles.menuSectionLabel}>{group.label}</div>
                  <div className={styles.menuSectionList}>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = item.path ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) : false;
                      return (
                        <Link
                          key={`mobile-auth-item-${item.path ?? item.label}`}
                          to={item.path ?? "#"}
                          className={`${styles.userMenuItem} ${active ? styles.userMenuItemActive : ""}`}
                          onClick={handleMobileLinkClick}
                        >
                          <span className={styles.userMenuIconWrap}><Icon className={styles.userMenuIcon} /></span>
                          <span className={styles.userMenuItemLabel}>{item.label}</span>
                          {item.badge ? (
                            <span
                              className={`${styles.userMenuBadge} ${
                                item.badge.type === "count"
                                  ? styles.userMenuBadgeCount
                                  : item.badge.variant === "beta"
                                    ? styles.userMenuBadgeBeta
                                    : styles.userMenuBadgeMuted
                              }`}
                            >
                              {item.badge.value}
                            </span>
                          ) : null}
                        </Link>
                      );
                    })}
                  </div>
                  {groupIndex < groupedUserMenu.length - 1 ? <div className={styles.menuDivider} /> : null}
                </React.Fragment>
              ))}
              <button
                className={styles.userMenuSignOut}
                onClick={handleLogout}
              >
                <FiLogOut className={styles.userMenuIcon} />
                Sign Out
              </button>
            </>
          ) : (
            <>
              {accessGroups.map((group, groupIndex) => (
                <React.Fragment key={`mobile-access-group-${group.label}`}>
                  <div className={styles.menuSectionLabel}>{group.label}</div>
                  <div className={styles.menuSectionList}>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = item.path ? location.pathname === item.path : false;
                      return (
                        <Link
                          key={`mobile-access-item-${item.path ?? item.label}`}
                          to={item.path ?? "#"}
                          className={`${styles.userMenuItem} ${active ? styles.userMenuItemActive : ""}`}
                          onClick={handleMobileLinkClick}
                        >
                          <span className={styles.userMenuIconWrap}><Icon className={styles.userMenuIcon} /></span>
                          <span className={styles.userMenuItemLabel}>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                  {groupIndex < accessGroups.length - 1 ? <div className={styles.menuDivider} /> : null}
                </React.Fragment>
              ))}
              {portalHost === "patient" ? (
                <Link
                  to={ROUTES.register}
                  className={`btn btn-primary btn-sm ${styles.mobileBtn}`}
                  onClick={handleMobileLinkClick}
                >
                  Start Care With Confidence
                </Link>
              ) : null}
            </>
          )}
          <button
            type="button"
            className={styles.mobileUtilityLink}
            onClick={() => {
              handleMobileLinkClick();
              openPreferenceCenter();
            }}
          >
            Cookie Preferences
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
